/**
 * The payslip PDF downloads — roadmap 2026-09-10 §9 C / U26, the web half.
 *
 * Both routes exist since Task 22 (Phase 8b), and both are sensitive reads:
 *
 *   GET /payroll/my-payslip.pdf?period_id=                    the caller's own, self-only
 *   GET /payroll/runs/{id}/items/{employeeId}/payslip.pdf     one team payslip, released runs
 *
 * `X-Access-Reason` is mandatory on each, and a `wp_data_access_logs` row is written
 * before a byte is rendered — even for the caller's own payslip. So this is the HR
 * document download (`hr/employee-document.ts`), pointed elsewhere:
 *
 *  - `client.sensitiveDownload` sends the reason as a header on a `fetch`. The payslip
 *    routes answer `200 application/pdf` directly rather than redirecting, and
 *    `redirect: 'follow'` on a response that is not a redirect is that response.
 *  - `deliverBlobDownload` hands the bytes to the browser.
 *  - `isUsableReason` refuses a missing, short or non-Latin-1 reason here, before any
 *    request, with the code the server would answer — no access-log row is possible for a
 *    request that was never made.
 *
 * The file name is rebuilt from the ids (`payslipPdfFilename`): `sensitiveDownload`
 * returns the bytes, not the `Content-Disposition` header.
 */

import type { ApiClient } from '../api/client';
import { CLIENT_TRANSPORT_FAILED, WorkforceApiError } from '../api/errors';
import { isUsableReason } from '../access/reason';
// The browser side effect and its type are generic; reused rather than re-implemented.
import { deliverBlobDownload, type DeliverBlob } from '../recruiting/candidate';

export { deliverBlobDownload, type DeliverBlob };

/** What a screen may remember about the payslip download it asked for. Holds no URL. */
export type PayslipDownloadState =
  | { readonly status: 'idle' }
  | { readonly status: 'requesting'; readonly key: string }
  | { readonly status: 'delivered'; readonly key: string; readonly filename: string }
  | { readonly status: 'refused'; readonly key: string; readonly error: WorkforceApiError };

export interface PayslipDownloadRequest {
  /** What the outcome is shown beside: `MY_PAYSLIP_DOWNLOAD_KEY`, or a team item's id. */
  readonly key: string;
  readonly path: string;
  readonly filename: string;
}

/**
 * The prefix my own payslip's download is keyed under. `PayrollScreen` keys the actual
 * request `my:<periodId>` (R11) rather than this bare prefix, so switching the typed
 * period never leaves a stale outcome showing under the payslip now on screen. Item ids
 * are digits, so this prefix can never collide with a team payslip's own key.
 */
export const MY_PAYSLIP_DOWNLOAD_KEY = 'my';

/**
 * The my-payslip download's actual key, `my:<periodId>` (R11) — the one place this is
 * built. `PayrollScreen` (which sends the download under this key) and `MyPayslipPanel`
 * (which decides whether a given outcome is its own to show) both call this rather than
 * each writing `` `${MY_PAYSLIP_DOWNLOAD_KEY}:${periodId}` `` independently: two
 * hand-written copies of the same string agree only by coincidence, and a later edit to
 * either one would silently and permanently stop `mine` from ever matching again, with
 * no type error to catch it (fix round 1).
 */
export function myPayslipDownloadKey(periodId: string): string {
  return `${MY_PAYSLIP_DOWNLOAD_KEY}:${periodId}`;
}

/**
 * The `403 WORKFORCE_ACCESS_REASON_REQUIRED` this client raises itself when the reason is
 * unusable — the server's own code, so `ApiErrorNote` renders the same state whoever
 * raised it.
 */
export function payslipAccessRefusal(): WorkforceApiError {
  return new WorkforceApiError({
    code: 'WORKFORCE_ACCESS_REASON_REQUIRED',
    message: 'An access reason of at least ten characters is required to download a payslip.',
    status: 403,
  });
}

export async function downloadPayslipPdf(
  client: Pick<ApiClient, 'sensitiveDownload'>,
  request: PayslipDownloadRequest,
  reason: string,
  deliver: DeliverBlob,
): Promise<PayslipDownloadState> {
  if (!isUsableReason(reason)) {
    return { status: 'refused', key: request.key, error: payslipAccessRefusal() };
  }
  try {
    const blob = await client.sensitiveDownload(request.path, reason.trim());
    deliver(blob, request.filename);
    return { status: 'delivered', key: request.key, filename: request.filename };
  } catch (cause) {
    return {
      status: 'refused',
      key: request.key,
      error:
        cause instanceof WorkforceApiError
          ? cause
          : new WorkforceApiError({
              code: CLIENT_TRANSPORT_FAILED,
              message: cause instanceof Error ? cause.message : 'download failed',
              status: 0,
            }),
    };
  }
}
